'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useReveal } from '@/components/marketing/engine/use-reveal';
import { Logo } from '@/components/marketing/hero/logo';
import { VaultExp } from './products/vault-exp';
import { InterviewExp } from './products/interview-exp';
import { ReportsExp } from './products/reports-exp';
import { BGVExp } from './products/bgv-exp';

/* v7 Command Center — cockpit of four instruments. Left narrative · right live experience.
   Cursor-reactive border · cinematic fade+slide · green/indigo only. */

interface Product {
  id: string;
  name: string;
  tag: string;
  headline: string;
  points: string[];
  note: string;
  Comp: React.ComponentType;
  icon: React.ReactNode;
}

const PRODUCTS: Product[] = [
  {
    id: 'vault', name: 'Vault', tag: 'Proctored assessment',
    headline: 'Run structured assessments.',
    points: ['Role-based assessment workflows', 'Candidate permissions requested before monitored sessions', 'Integrity signals available for reviewer context'],
    note: 'Monitoring and review depend on the assessment configuration and candidate consent.',
    Comp: VaultExp,
    icon: <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z M9 12l2 2 4-4" />,
  },
  {
    id: 'interview', name: 'Interview', tag: 'Structured interviews',
    headline: 'Keep interviews in the hiring workflow.',
    points: ['Schedule and host structured interviews', 'Keep completed-session records with the application', 'Give reviewers a consistent place for feedback'],
    note: 'Available records depend on the interview configuration and completed session.',
    Comp: InterviewExp,
    icon: <path d="M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z M5 11a7 7 0 0 0 14 0 M12 18v4" />,
  },
  {
    id: 'reports', name: 'AI Reports', tag: 'Candidate reports',
    headline: 'Review available candidate information together.',
    points: ['Summaries drawn from available application records', 'Source material remains available to reviewers', 'Hiring teams retain the decision'],
    note: 'AI-assisted summaries support review; they do not replace recruiter judgment.',
    Comp: ReportsExp,
    icon: <path d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z M9 8h6 M9 12h6 M9 16h3" />,
  },
  {
    id: 'bgv', name: 'BGV', tag: 'Background verification',
    headline: 'Coordinate configured background checks.',
    points: ['Request the checks selected for a role', 'Keep check status with the candidate record', 'Review returned discrepancies before a decision'],
    note: 'Coverage, timing, and verification sources vary by check and provider.',
    Comp: BGVExp,
    icon: <path d="M16 7a4 4 0 1 0-8 0 4 4 0 0 0 8 0z M4 21v-1a6 6 0 0 1 6-6h1 M15 19l2 2 4-4" />,
  },
];

export function CommandCenter() {
  const { ref, shown } = useReveal(0.1);
  const [active, setActive] = useState<number>(0);
  const [key, setKey] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const pauseRef = useRef<boolean>(false);

  const select = (i: number) => {
    setActive(i);
    setKey(k => k + 1);
  };

  useEffect(() => {
    if (!shown) return;
    const iv = setInterval(() => {
      if (!pauseRef.current) {
        setActive(a => {
          setKey(k => k + 1);
          return (a + 1) % PRODUCTS.length;
        });
      }
    }, 9000);
    return () => clearInterval(iv);
  }, [shown]);

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  const P = PRODUCTS[active];

  return (
    <section ref={ref} id="command" className="section" style={{ background: 'var(--bg-veil)', overflow: 'hidden' }}>
      <div className="section-inner" style={{ position: 'relative', zIndex: 1, maxWidth: '1160px' }}>
        <div style={{ textAlign: 'center', maxWidth: '620px', margin: '0 auto 52px' }}>
          <span className="label" style={{ color: 'var(--headline-accent)', display: 'block', marginBottom: '16px', opacity: shown ? 1 : 0, transition: 'opacity .6s' }}>02 · VERIFICATION</span>
          <h2 className="serif" style={{ fontSize: 'var(--h2)', lineHeight: 1.04, letterSpacing: '-0.01em',
            opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(14px)', transition: 'all .9s var(--ease) .1s' }}>
            One workspace.<br /><span style={{ fontStyle: 'italic', color: 'var(--headline-accent)' }}>Four instruments of proof.</span>
          </h2>
        </div>

        {/* Cockpit container with cursor-reactive border */}
        <div ref={containerRef} onMouseMove={onMove}
          onMouseEnter={() => { pauseRef.current = true; }} onMouseLeave={() => { pauseRef.current = false; }}
          style={{ position: 'relative', borderRadius: 'var(--r-xl)', padding: '1.5px',
            opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(22px)',
            transition: 'opacity .9s var(--ease) .2s, transform .9s var(--ease) .2s' }}>
          {/* gradient border layer */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: 'var(--r-xl)', padding: '1.5px', pointerEvents: 'none',
            background: 'radial-gradient(300px circle at var(--mx, 50%) var(--my, 0%), var(--indigo-light), transparent 60%), linear-gradient(var(--border), var(--border))',
            WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' }}></div>

          <div style={{ position: 'relative', borderRadius: 'calc(var(--r-xl) - 1.5px)', overflow: 'hidden',
            background: 'var(--glass)', backdropFilter: 'blur(28px) saturate(1.3)', WebkitBackdropFilter: 'blur(28px) saturate(1.3)',
            boxShadow: 'var(--shadow)' }}>
            {/* environmental glow tied to active instrument */}
            <div style={{ position: 'absolute', top: '-25%', left: `${10 + active * 24}%`, width: '38%', height: '70%',
              background: 'radial-gradient(ellipse, var(--spill), transparent 70%)', filter: 'blur(46px)', transition: 'left .8s var(--ease)', pointerEvents: 'none' }}></div>

            {/* header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 22px', borderBottom: '1px solid var(--border)', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                <Logo size={20} />
                <span className="mono" style={{ fontSize: '10px', color: 'var(--text-3)' }}>vhire · workspace</span>
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--green)', animation: 'spill-pulse 2s ease infinite' }}></span>
                <span className="mono" style={{ fontSize: '9px', color: 'var(--green)' }}>LIVE</span>
              </span>
            </div>

            {/* cinematic split */}
            <div style={{ padding: '30px', position: 'relative' }}>
              <div key={key} className="cmd-split" style={{ display: 'grid', gridTemplateColumns: '0.78fr 1.22fr', gap: '36px', alignItems: 'center', minHeight: 'clamp(380px, 46vw, 440px)', animation: 'cmd-enter .65s var(--ease)' }}>
                {/* LEFT — narrative */}
                <div>
                  <span className="label" style={{ color: 'var(--indigo-light)', display: 'block', marginBottom: '14px' }}>{P.tag}</span>
                  <h3 style={{ fontSize: 'var(--h3)', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.1, marginBottom: '20px' }}>{P.headline}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '22px' }}>
                    {P.points.map((pt, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '11px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}><path d="M20 6L9 17l-5-5"/></svg>
                        <span style={{ fontSize: '13.5px', color: 'var(--text)', lineHeight: 1.45 }}>{pt}</span>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--text-3)', lineHeight: 1.6, borderLeft: '2px solid var(--border-hi)', paddingLeft: '14px' }}>{P.note}</p>
                </div>

                {/* RIGHT — live experience */}
                <div className="cmd-stage">
                  <P.Comp />
                </div>
              </div>
            </div>

            {/* Dock */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '0 16px 20px' }}>
              <div style={{ display: 'flex', gap: '4px', padding: '6px', borderRadius: 'var(--r-full)',
                background: 'var(--surface)', border: '1px solid var(--border)', backdropFilter: 'blur(10px)' }}>
                {PRODUCTS.map((p, i) => {
                  const on = active === i;
                  return (
                    <button key={p.id} className="cmd-dock-btn" onClick={() => select(i)} title={p.name} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: on ? '8px' : '0', padding: on ? '9px 17px' : '9px',
                      borderRadius: 'var(--r-full)', border: 'none', cursor: 'pointer',
                      background: on ? 'var(--indigo)' : 'transparent', color: on ? '#fff' : 'var(--text-3)',
                      transition: 'all .4s var(--ease)', overflow: 'hidden', whiteSpace: 'nowrap' }}
                      onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => { if (!on) e.currentTarget.style.color = 'var(--text)'; }}
                      onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => { if (!on) e.currentTarget.style.color = 'var(--text-3)'; }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{p.icon}</svg>
                      <span style={{ fontSize: '12.5px', fontWeight: 600, maxWidth: on ? '140px' : '0', opacity: on ? 1 : 0, transition: 'all .4s var(--ease)' }}>{p.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes cmd-enter { from { opacity: 0; transform: translateY(12px) scale(0.99); } to { opacity: 1; transform: none; } }
        #command .cmd-stage { height: clamp(360px, 44vw, 420px); }
        #command .cmd-stage > * { height: 100%; }
        @media (max-width: 820px) {
          #command .cmd-split { grid-template-columns: 1fr !important; gap: 24px !important; align-items: stretch !important; }
          #command .exp-split { grid-template-columns: 1fr !important; }
          #command .cmd-stage { height: auto !important; }
          #command .cmd-stage > * { height: auto !important; }
          #command .cmd-dock-btn { min-width: 44px; min-height: 44px; }
        }
      `}</style>
    </section>
  );
}
